import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import CustomSelect from 'components/Form/CustomSelect';
import { PhysicalAssetType } from '../types/on-boarding.type';
import {
  useCreatePhysicalAsset,
  useDeletePhysicalAsset,
  useGetPhysicalAssetList
} from 'common/queries-and-mutations/physical-asset';
import { Loader } from 'components/Loader';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import InfoBar from './InfoBar';
import { STEPS } from 'constants/steps';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { fetchUserInformation } from 'thunks/account-thunk';
import { useSkipOnboardingStep } from 'common/queries-and-mutations/user';

export default function PhysicalAssetInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isLoading, isError } = useCreatePhysicalAsset();
  const { mutate: deletePhysical, isLoading: isDeleting } =
    useDeletePhysicalAsset();
  const { mutate: skipOnboarding, isSuccess: skipSuccess } =
    useSkipOnboardingStep();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isAssetSuccess,
    isLoading: isAssetLoading
  } = useGetPhysicalAssetList();

  const physicalAsset = useMemo(() => {
    if (isAssetSuccess && data.length > 0) {
      return data.map(asset => ({
        physicalAssetId: asset.id,
        assetType: asset.assetType,
        description: asset.description,
        location: asset.location,
        regNo: asset.regNo
      }));
    } else {
      return [
        {
          physicalAssetId: null,
          assetType: '',
          description: '',
          location: '',
          regNo: ''
        }
      ];
    }
  }, [data, isAssetSuccess]);

  const formik = useFormik({
    initialValues: {
      physicalAsset
    },
    validationSchema: Yup.object({
      physicalAsset: Yup.array().of(
        Yup.object().shape({
          assetType: Yup.string().required('Asset type is required').nullable(),
          description: Yup.string()
            .required('Description is required')
            .nullable(),
          // location: Yup.string()
          //   .required('Location of the asset is required')
          //   .nullable(),
          regNo: Yup.string().required('Asset No is required')
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onPhysicalSubmit(values);
    }
  });

  const addMorePhysical = () => {
    formik.values.physicalAsset.push({
      physicalAssetId: null,
      assetType: '',
      description: '',
      location: '',
      regNo: ''
    });
    formik.setFieldValue('physicalAsset', [...formik.values.physicalAsset]);
  };

  const removePhysical = (
    indexToRemove: number,
    physicalAssetId?: number | null
  ) => {
    if (physicalAssetId) {
      deletePhysical(physicalAssetId);
    }
    formik.values.physicalAsset.splice(indexToRemove, 1);
    formik.setFieldValue('physicalAsset', [...formik.values.physicalAsset]);
  };

  const onPhysicalSubmit = (formValue: PhysicalAssetType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.PHYSICAL_ASSETS_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(
      findPreviousPage(planAllowPage, STEPS.PHYSICAL_ASSETS_INFORMATION)
    );
  };

  const handleSkip = () => {
    setIsSubmitting(true);
    skipOnboarding();
  };

  useEffect(() => {
    if (skipSuccess) {
      dispatch(fetchUserInformation());
      navigate('/dashboard');
    }
  }, [skipSuccess, dispatch]);

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={2}
        stepPercentage={STEPS.PHYSICAL_ASSETS_INFORMATION}
        timelineStep={2}
      />
      {isAssetLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="flex w-full items-end justify-between">
            <span></span>
            <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
              Physical Asset
            </p>
            <button
              type="button"
              className="mt-5 text-sm font-normal text-[#353535] underline"
              onClick={handleSkip}
            >
              Skip to Dashboard
            </button>
          </div>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.physicalAsset.map((physical, index) => (
              <div key={index} className="mb-1 mt-8">
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={[
                        { label: 'Fixed Asset', value: 'Fixed Asset' },
                        { label: 'Movable Asset', value: 'Movable Asset' },
                        {
                          label: 'Other Valuable Asset',
                          value: 'Other Valuable Asset'
                        }
                      ]}
                      selectedOption={{
                        value: physical.assetType,
                        label: physical.assetType
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `physicalAsset[${index}].assetType`,
                          option?.value
                        );
                      }}
                      label="Asset Type"
                      name="physicalAsset"
                      isRequired
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      error={
                        formik.touched.physicalAsset
                          ? formik.errors.physicalAsset?.[index] &&
                            formik.errors.physicalAsset.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.physicalAsset[index]
                                )
                              ).physicalAsset
                            : ''
                          : ''
                      }
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`physicalAsset[${index}].description`}
                      placeholder=""
                      value={physical.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Description of Asset"
                      error={
                        formik.touched.physicalAsset
                          ? formik.errors.physicalAsset?.[index] &&
                            formik.errors.physicalAsset.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.physicalAsset[index]
                                )
                              ).description
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`physicalAsset[${index}].location`}
                      placeholder=""
                      value={physical.location}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Location of Asset"
                      error={
                        formik.touched.physicalAsset
                          ? formik.errors.physicalAsset?.[index] &&
                            formik.errors.physicalAsset.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.physicalAsset[index]
                                )
                              ).location
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`physicalAsset[${index}].regNo`}
                      placeholder=""
                      value={physical.regNo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Asset Unique ID/Reg No"
                      error={
                        formik.touched.physicalAsset
                          ? formik.errors.physicalAsset?.[index] &&
                            formik.errors.physicalAsset.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.physicalAsset[index]
                                )
                              ).regNo
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                {showRemoveButton(
                  index,
                  physical.physicalAssetId,
                  data!.length > 1
                ) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          removePhysical(index, physical.physicalAssetId)
                        }
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div className="flex items-center justify-between gap-2">
              <div className="flex w-full items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              <div className="group z-0 flex w-full items-center justify-end">
                <div
                  className="flex items-center gap-4"
                  onClick={addMorePhysical}
                  role="button"
                  tabIndex={-1}
                >
                  <Icon name={Icons.AddCircleOutline} />
                  <span className="mt-2 text-base font-medium text-[#353535]">
                    Add Another Asset
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-1 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-5 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
